import axios from 'axios';
import { ProductImage } from '../types/product';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

export const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Types based on backend entities
export type LocationType = 'store' | 'warehouse';
export type Currency = 'DZD' | 'EUR';

export interface Location {
  id: string;
  organizationId: string;
  name: string;
  slug?: string;
  code?: string;
  type: LocationType;
  description?: string;
  address?: string;
  city?: string;
  wilaya?: string;
  state?: string;
  zipCode?: string;
  country: string;
  latitude?: number;
  longitude?: number;
  currency?: Currency;
  phone?: string;
  email?: string;
  logo?: string;
  canSellToCustomers: boolean;
  canFulfillOnlineOrders: boolean;
  canReceiveTransfers: boolean;
  canSendTransfers: boolean;
  managerId?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// Backward compatible alias for customer-facing store concept
export type Store = Location;

export interface Product {
  id: string;
  name: string;
  description?: string;
  price?: number | string; // API returns string, parse as needed
  category?: string;
  brand?: string;
  tags?: string[];
  images?: ProductImage[];
  locationId?: string | null; // Optional - products are organization-level catalog items
  isActive: boolean;
  isAvailable?: boolean;
  isPreorderable?: boolean; // Allows pre-orders when product is out of stock
  attributes?: Record<string, string>;
  sku?: string;
  location?: Location;
  inventory?: Inventory;
  organizationId?: string;
}

export interface Inventory {
  id: string;
  productId: string;
  quantity: number;
  reserved: number;
  lowStockThreshold: number;
  storageLocation?: string;
  product?: Product;
}

export interface CreateReservationDto {
  customerId?: string;
  customerName?: string;
  customerEmail?: string;
  customerPhone?: string;
  notes?: string;
  items: {
    productId: string;
    quantity: number;
  }[];
}

// API Services - Locations (customer-facing stores)
export const locationsApi = {
  // Get all active stores (public endpoint for storefront)
  getAll: () => apiClient.get<Location[]>('/locations/stores').then((res) => res.data),

  getById: (id: string) =>
    apiClient.get<Location>(`/locations/${id}`).then((res) => res.data),

  // Get active stores (same as getAll since /stores only returns active stores with storefront access)
  getActive: () =>
    apiClient.get<Location[]>('/locations/stores').then((res) => res.data),

  // Get only stores (customer-facing locations) - uses public stores endpoint
  getStores: () =>
    apiClient.get<Location[]>('/locations/stores').then((res) => res.data),
};

// Backward compatible alias
export const storesApi = locationsApi;

export const organizationsApi = {
  // Get an organization's tax rate (public endpoint for storefront)
  getTaxRate: (organizationId: string) =>
    apiClient
      .get<{ taxRate: number }>(`/organizations/${organizationId}/tax-rate`)
      .then((res) => res.data),
};

export const productsApi = {
  getAll: (params?: { locationId?: string; category?: string; search?: string }) =>
    apiClient.get<{ data: Product[]; total: number }>('/products', {
      params: { ...params, limit: 100 } // Max limit enforced by backend validation
    }).then((res) => {
      // Handle paginated response - extract data array
      return Array.isArray(res.data) ? res.data : res.data.data;
    }),

  // Get total count of products (for stats display)
  getTotal: () =>
    apiClient.get<{ data: Product[]; total: number }>('/products', {
      params: { limit: 1 }
    }).then((res) => {
      return res.data.total || (Array.isArray(res.data) ? res.data.length : res.data.data?.length || 0);
    }),

  getById: (id: string) =>
    apiClient.get<Product>(`/products/${id}`).then((res) => res.data),

  getByLocation: (locationId: string) =>
    apiClient.get<{ data: Product[]; total: number }>(`/products`, {
      params: { locationId, limit: 100 } // Max limit enforced by backend validation
    }).then((res) => {
      // Handle paginated response - extract data array
      return Array.isArray(res.data) ? res.data : res.data.data;
    }),

};

// Public-facing inventory shape returned by GET /inventory/product/:productId.
// Mirrors the backend PublicInventoryDto: raw quantity/reserved are intentionally
// NOT exposed to the storefront; consumers use `available`/`inStock`/`lowStock`.
export interface PublicInventory {
  productId: string;
  available: number;
  inStock: boolean;
  lowStock?: boolean;
}

export const inventoryApi = {
  getByProduct: (productId: string) =>
    apiClient.get<PublicInventory>(`/inventory/product/${productId}`).then((res) => res.data),

  // Bulk availability for the storefront product list. Uses the PUBLIC endpoint so anonymous
  // (not-logged-in) customers can read stock without a 401. The server returns the safe
  // PublicInventoryDto shape (available/inStock/lowStock) with availability already computed —
  // internal quantity/reserved are intentionally never exposed, so there is nothing to recompute.
  getByLocation: (locationId: string): Promise<PublicInventory[]> =>
    apiClient.get<PublicInventory[] | { data: PublicInventory[] }>(`/inventory/public/location/${locationId}`).then((res) => {
      return Array.isArray(res.data) ? res.data : (res.data as { data: PublicInventory[] }).data || [];
    }),

  checkAvailability: (productId: string, quantity: number) =>
    apiClient
      .get<{ available: boolean; quantityAvailable: number }>(
        `/inventory/product/${productId}/check`,
        { params: { quantity } }
      )
      .then((res) => res.data),
};

// Customer credit ("carnet de crédit"), read-only self-service view.
// Mirrors the backend CreditSelfSummary returned by the self-service endpoint: the caller's own account plus
// its ledger rows, with the joined `customer` object deliberately omitted server-side.
export type CreditAccountStatus = 'ACTIVE' | 'SUSPENDED' | 'CLOSED';
export type CreditTransactionType = 'CHARGE' | 'PAYMENT' | 'ADJUSTMENT';

export interface CreditTransaction {
  id: string;
  creditAccountId: string;
  organizationId: string;
  type: CreditTransactionType;
  // Stored positive except for signed manual adjustments; the type carries the ledger sign
  // (CHARGE adds debt, PAYMENT and ADJUSTMENT subtract it).
  amount: number;
  orderId?: string | null;
  recordedByUserId: string;
  note?: string | null;
  // Append-only ledger: rows are never mutated, so there is no updatedAt.
  createdAt: string;
}

export interface MyCreditSummary {
  id: string;
  organizationId: string;
  customerId: string;
  // numeric(10,2) columns, converted by the backend's DecimalTransformer, so they arrive as
  // JSON numbers rather than the strings pg returns for decimals.
  creditLimit: number;
  balance: number;
  status: CreditAccountStatus;
  createdAt: string;
  updatedAt: string;
  transactions: CreditTransaction[];
}

export const creditApi = {
  // Self-service only: the account is resolved from the JWT, there is no id parameter.
  // Returns 403 when the token carries no organization.
  getMyCredit: () =>
    apiClient.get<MyCreditSummary>('/credit/me').then((res) => res.data),
};

export const reservationsApi = {
  create: (data: CreateReservationDto) =>
    apiClient.post('/reservations', data).then((res) => res.data),

  getById: (id: string) =>
    apiClient.get(`/reservations/${id}`).then((res) => res.data),
};
